import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import {
  Search, Clock, CalendarDays, ArrowRight, Loader2,
  Newspaper, AlertCircle, X, Sparkles,
} from 'lucide-react';
import BlogCard, { AuthorLine, formatBlogDate } from '@/components/blog/BlogCard';

const API_BASE = 'https://xceededge.com';
const PAGE_SIZE = 9;

const FALLBACK_COVER =
  'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=1200&q=80';

export default function Blog() {
  const [posts, setPosts]           = useState([]);
  const [featured, setFeatured]     = useState(null);
  const [categories, setCategories] = useState([]);
  const [total, setTotal]           = useState(0);
  const [hasMore, setHasMore]       = useState(false);

  const [search, setSearch]     = useState('');
  const [query, setQuery]       = useState('');   // debounced copy of `search`
  const [category, setCategory] = useState('all');

  const [loading, setLoading]         = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError]             = useState('');

  const isFiltered = query.trim() !== '' || category !== 'all';

  // Debounce typing so a search doesn't fire a request per keystroke.
  useEffect(() => {
    const t = setTimeout(() => setQuery(search), 400);
    return () => clearTimeout(t);
  }, [search]);

  const fetchPage = useCallback(async (offset = 0) => {
    offset === 0 ? setLoading(true) : setLoadingMore(true);
    setError('');

    try {
      const res = await axios.get(`${API_BASE}/get-blogs.php`, {
        params: { action: 'list', search: query, category, limit: PAGE_SIZE, offset },
      });

      if (!res.data?.success) throw new Error(res.data?.message || 'Failed to load');

      const batch = res.data.data || [];
      setPosts((prev) => (offset === 0 ? batch : [...prev, ...batch]));
      setTotal(res.data.total || 0);
      setHasMore(!!res.data.has_more);
      if (offset === 0) {
        setCategories(res.data.categories || []);
        setFeatured(res.data.featured || null);
      }
    } catch {
      setError('We could not load the blog right now. Please try again in a moment.');
      if (offset === 0) { setPosts([]); setFeatured(null); }
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [query, category]);

  useEffect(() => { fetchPage(0); }, [fetchPage]);

  // The hero already shows the newest post, so drop it from the grid on the
  // unfiltered first view to avoid rendering the same post twice.
  const showHero = !isFiltered && !!featured;
  const gridPosts = showHero ? posts.filter((p) => p.id !== featured.id) : posts;

  const clearFilters = () => { setSearch(''); setQuery(''); setCategory('all'); };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="pb-20">
      {/* ── Header ─────────────────────────────────────────────── */}
      <section className="border-b bg-gradient-to-b from-primary/5 to-transparent pt-28 pb-12">
        <div className="mx-auto max-w-6xl px-4 sm:px-6">
          <span className="inline-flex items-center gap-1.5 rounded-full border bg-background px-3 py-1 text-xs font-semibold text-primary">
            <Sparkles className="h-3.5 w-3.5" /> Insights from the XceedEdge team
          </span>

          <h1 className="mt-4 font-heading text-3xl font-bold tracking-tight sm:text-5xl">
            XceedEdge <span className="text-primary">Blog</span>
          </h1>
          <p className="mt-3 max-w-2xl text-base text-muted-foreground sm:text-lg">
            Career roadmaps, curriculum deep-dives and student success stories — written by the
            mentors who teach our programs.
          </p>

          {/* Search */}
          <div className="relative mt-7 max-w-xl">
            <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search articles, topics or authors…"
              className="w-full rounded-full border bg-background py-3 pl-11 pr-11 text-sm shadow-sm transition focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/25"
            />
            {search && (
              <button
                onClick={() => setSearch('')}
                aria-label="Clear search"
                className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full p-1.5 text-muted-foreground transition hover:bg-muted hover:text-foreground"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>

          {/* Category chips */}
          {categories.length > 0 && (
            <div className="mt-5 flex flex-wrap gap-2">
              <FilterChip active={category === 'all'} onClick={() => setCategory('all')}>
                All posts
              </FilterChip>
              {categories.map((c) => (
                <FilterChip key={c.name} active={category === c.name} onClick={() => setCategory(c.name)}>
                  {c.name}
                  <span className="ml-1.5 opacity-60">{c.count}</span>
                </FilterChip>
              ))}
            </div>
          )}
        </div>
      </section>

      <div className="mx-auto max-w-6xl px-4 pt-10 sm:px-6">
        {/* ── Error ─────────────────────────────────────────────── */}
        {error && !loading && (
          <div className="flex flex-col items-center gap-3 rounded-2xl border border-destructive/30 bg-destructive/5 p-8 text-center">
            <AlertCircle className="h-6 w-6 text-destructive" />
            <p className="text-sm text-muted-foreground">{error}</p>
            <button
              onClick={() => fetchPage(0)}
              className="rounded-full bg-primary px-5 py-2 text-sm font-semibold text-primary-foreground transition hover:brightness-95"
            >
              Try again
            </button>
          </div>
        )}

        {/* ── Loading ───────────────────────────────────────────── */}
        {loading && <BlogSkeleton />}

        {/* ── Empty ─────────────────────────────────────────────── */}
        {!loading && !error && posts.length === 0 && (
          <div className="flex flex-col items-center gap-3 rounded-2xl border bg-card p-12 text-center">
            <Newspaper className="h-8 w-8 text-muted-foreground/50" />
            <h2 className="font-heading text-lg font-bold">
              {isFiltered ? 'No articles match your search' : 'No articles published yet'}
            </h2>
            <p className="max-w-md text-sm text-muted-foreground">
              {isFiltered
                ? 'Try a different keyword, or browse every post.'
                : 'Our first posts are on the way. Check back shortly.'}
            </p>
            {isFiltered && (
              <button
                onClick={clearFilters}
                className="mt-1 rounded-full border px-5 py-2 text-sm font-semibold transition hover:bg-muted"
              >
                Clear filters
              </button>
            )}
          </div>
        )}

        {/* ── Featured hero ─────────────────────────────────────── */}
        {!loading && !error && showHero && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45 }}
            className="mb-12"
          >
            <Link
              to={`/blog/${featured.slug}`}
              className="group grid overflow-hidden rounded-3xl border bg-card transition-all duration-300 hover:shadow-2xl hover:border-primary/40 lg:grid-cols-2"
            >
              <div className="relative aspect-[16/10] overflow-hidden bg-muted lg:aspect-auto lg:min-h-[22rem]">
                <img
                  src={featured.cover_image || FALLBACK_COVER}
                  alt={featured.title}
                  className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  onError={(e) => { e.currentTarget.src = FALLBACK_COVER; }}
                />
                <span className="absolute left-4 top-4 rounded-full bg-primary px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-primary-foreground">
                  Featured
                </span>
              </div>

              <div className="flex flex-col justify-center p-6 sm:p-9">
                <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                  {featured.category && (
                    <span className="rounded-full bg-primary/10 px-2.5 py-1 font-semibold text-primary">
                      {featured.category}
                    </span>
                  )}
                  {featured.published_at && (
                    <span className="flex items-center gap-1">
                      <CalendarDays className="h-3 w-3" /> {formatBlogDate(featured.published_at)}
                    </span>
                  )}
                  {featured.read_time && (
                    <span className="flex items-center gap-1">
                      <Clock className="h-3 w-3" /> {featured.read_time}
                    </span>
                  )}
                </div>

                <h2 className="mt-3 font-heading text-2xl font-bold leading-tight transition-colors group-hover:text-primary sm:text-3xl">
                  {featured.title}
                </h2>

                {featured.excerpt && (
                  <p className="mt-3 text-sm leading-relaxed text-muted-foreground line-clamp-3 sm:text-base">
                    {featured.excerpt}
                  </p>
                )}

                <div className="mt-6 flex items-center justify-between gap-4">
                  <AuthorLine post={featured} />
                  <span className="flex items-center gap-1.5 text-sm font-semibold text-primary">
                    Read article <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </span>
                </div>
              </div>
            </Link>
          </motion.div>
        )}

        {/* ── Grid ──────────────────────────────────────────────── */}
        {!loading && !error && gridPosts.length > 0 && (
          <>
            <div className="mb-5 flex items-baseline justify-between gap-3">
              <h2 className="font-heading text-xl font-bold">
                {isFiltered ? 'Results' : 'Latest articles'}
              </h2>
              <p className="text-sm text-muted-foreground">
                {total} {total === 1 ? 'article' : 'articles'}
              </p>
            </div>

            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {gridPosts.map((post, i) => (
                <BlogCard key={post.id} post={post} index={i} />
              ))}
            </div>
          </>
        )}

        {/* ── Load more ─────────────────────────────────────────── */}
        {!loading && !error && hasMore && (
          <div className="mt-10 flex justify-center">
            <button
              onClick={() => fetchPage(posts.length)}
              disabled={loadingMore}
              className="inline-flex items-center gap-2 rounded-full border px-6 py-3 text-sm font-semibold transition hover:bg-muted disabled:opacity-60"
            >
              {loadingMore ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              {loadingMore ? 'Loading…' : 'Load more articles'}
            </button>
          </div>
        )}
      </div>
    </motion.div>
  );
}

function FilterChip({ active, onClick, children }) {
  return (
    <button
      onClick={onClick}
      className={`rounded-full px-3.5 py-1.5 text-sm font-medium transition-all ${
        active
          ? 'bg-primary text-primary-foreground shadow-sm'
          : 'bg-muted text-muted-foreground hover:bg-muted/70 hover:text-foreground'
      }`}
    >
      {children}
    </button>
  );
}

function BlogSkeleton() {
  return (
    <div className="grid animate-pulse gap-6 sm:grid-cols-2 lg:grid-cols-3">
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className="overflow-hidden rounded-2xl border bg-card">
          <div className="aspect-[16/9] bg-muted" />
          <div className="space-y-3 p-5">
            <div className="h-3 w-24 rounded bg-muted" />
            <div className="h-5 w-full rounded bg-muted" />
            <div className="h-5 w-2/3 rounded bg-muted" />
            <div className="h-3 w-full rounded bg-muted" />
            <div className="h-3 w-5/6 rounded bg-muted" />
          </div>
        </div>
      ))}
    </div>
  );
}
